import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { defaultDateFormat } from "./dateFormats";
import * as Styled from "./DateInput.styled";
import { useDateMask } from "./useDateMask";
import { areDatesDifferent, isValidDate, parseDate } from "./utils";
import { stringMapDefault } from "./DateInput.stringMap";
const componentName = "DateInput";
export const DateInput = forwardRef((props, ref) => {
    const { date, onChange: onChangeProp, onDateChange, placeholder, stringMap: stringMapProp, validate = defaults.validate, dateFormat = defaults.dateFormat, children, onBlur, hasFocus, onKeyUp, onKeyDown, ...inputProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const inputRef = useRef(null);
    const [typedValue, setTypedValue] = useState(date && isValidDate(date) ? format(date, dateFormat) : "");
    const updateTypedValue = useCallback((value) => {
        setTypedValue(value);
        const parsedDate = parseDate(value, dateFormat, date);
        const isValid = parsedDate === null || isValidDate(parsedDate);
        if (validate !== false && inputRef.current) {
            const customError = typeof validate === "function" && validate({ value, date: parsedDate, isValidDate: isValid });
            const defaultError = isValid ? "" : stringMap.invalidDate || parsedDate.toString();
            inputRef.current.setCustomValidity(customError || defaultError);
        }
        return { parsedDate, isValid };
    }, [date, dateFormat, stringMap.invalidDate, validate]);
    const onChange = (e) => {
        const { value } = e.currentTarget;
        const { parsedDate, isValid } = updateTypedValue(value);
        if (validate && isValid) {
            onDateChange?.(parsedDate);
        }
        else if (areDatesDifferent(date, parsedDate)) {
            onDateChange?.(parsedDate);
        }
        onChangeProp?.(e);
    };
    // Refresh typed value if new date passed in
    useEffect(() => {
        if (!date) {
            updateTypedValue("");
        }
        else if (isValidDate(date)) {
            updateTypedValue(format(date, dateFormat));
        }
    }, [updateTypedValue, date, dateFormat]);
    const dateMaskProps = useDateMask({
        dateFormat,
        onChange,
        onKeyDown,
        onKeyUp
    });
    return (_jsx(Styled.DateInput, { ...inputProps, "data-testid": "DateInput", ref: useCombineRefs(inputRef, ref), value: typedValue, placeholder: placeholder || dateFormat.toLowerCase(), inputMode: "numeric", hasFocus: hasFocus, onChange: onChange, onBlur: (e) => {
            e.currentTarget.checkValidity();
            onBlur?.(e);
        }, ...dateMaskProps, children: children }));
});
DateInput.displayName = componentName;
const defaults = {
    validate: () => "",
    dateFormat: defaultDateFormat,
    // excluded from API because: https://github.com/date-fns/date-fns/issues/2849
    referenceDate: new Date()
};
export { areDatesDifferent, isValidDate, parseDate };
